import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const createEmptyTotals = () => ({
  currentRegistrations: 0,
  newUsers: 0,
  falloutBidders: 0,
  revivedBidders: 0,
  doaBidders: 0,
});

export default function useDailyRegistrationActivity({
  orgReady,
  tab,
  apiBaseUrl,
  routes,
  apiFetchJson,
  handleNeedsAuth,
  toastOpen,
  toAbsInt,
  safeNum,
  compareSortValues,
  utcMsToDateKey,
  addDaysToDateKeyUTC,
  addMonthsToDateKeyUTC,
  isDateKeyUTC,
  formatDateKeyRangeShortUTC,
  dateKeyToUtcMs,
  formatDateKeyShortUTC,
  weekStartDateKeyUTC,
  monthLabelFromKeyUTC,
}) {
  const [regActivityRangePreset, setRegActivityRangePreset] = useState("30d");
  const [regActivityCustomStartDate, setRegActivityCustomStartDate] = useState(() => {
    const today = utcMsToDateKey(Date.now());
    return addDaysToDateKeyUTC(today, -29) || today;
  });
  const [regActivityCustomEndDate, setRegActivityCustomEndDate] = useState(() => utcMsToDateKey(Date.now()));
  const [regActivityMeasure, setRegActivityMeasure] = useState("daily");
  const [regActivityRows, setRegActivityRows] = useState([]);
  const [regActivityTotals, setRegActivityTotals] = useState(createEmptyTotals);
  const [regActivityLoading, setRegActivityLoading] = useState(false);
  const [regActivitySortKey, setRegActivitySortKey] = useState("dateKey");
  const [regActivitySortDir, setRegActivitySortDir] = useState("asc");
  const [regActivityUsersOpen, setRegActivityUsersOpen] = useState(false);
  const [regActivityUsersLoading, setRegActivityUsersLoading] = useState(false);
  const [regActivityUsersError, setRegActivityUsersError] = useState("");
  const [regActivityUsersDateKey, setRegActivityUsersDateKey] = useState("");
  const [regActivityUsersType, setRegActivityUsersType] = useState("");
  const [regActivityUsersRows, setRegActivityUsersRows] = useState([]);
  const apiFetchJsonRef = useRef(apiFetchJson);
  const handleNeedsAuthRef = useRef(handleNeedsAuth);
  const toastOpenRef = useRef(toastOpen);

  useEffect(() => {
    apiFetchJsonRef.current = apiFetchJson;
  }, [apiFetchJson]);
  useEffect(() => {
    handleNeedsAuthRef.current = handleNeedsAuth;
  }, [handleNeedsAuth]);
  useEffect(() => {
    toastOpenRef.current = toastOpen;
  }, [toastOpen]);

  const regActivityRangeOptions = useMemo(
    () => [
      { value: "30d", label: "Last 30 Days" },
      { value: "90d", label: "Last 90 Days" },
      { value: "6m", label: "Last 6 Months" },
      { value: "1y", label: "Last 1 Year" },
      { value: "all", label: "All Time" },
      { value: "custom", label: "Custom Range" },
    ],
    []
  );

  const regActivityResolvedRange = useMemo(() => {
    const todayDateKey = utcMsToDateKey(Date.now());
    const buildLabel = (startDateKey, endDateKey, fallback = "") => {
      const formatted = formatDateKeyRangeShortUTC(startDateKey, endDateKey);
      return formatted || fallback || "Selected Range";
    };

    if (regActivityRangePreset === "all") {
      return {
        preset: "all",
        allTime: true,
        startDate: "",
        endDate: todayDateKey,
        label: "All Time",
      };
    }

    if (regActivityRangePreset === "custom") {
      let startDate = isDateKeyUTC(regActivityCustomStartDate) ? regActivityCustomStartDate : todayDateKey;
      let endDate = isDateKeyUTC(regActivityCustomEndDate) ? regActivityCustomEndDate : todayDateKey;
      if (startDate > endDate) {
        const tmp = startDate;
        startDate = endDate;
        endDate = tmp;
      }
      return {
        preset: "custom",
        allTime: false,
        startDate,
        endDate,
        label: buildLabel(startDate, endDate, "Custom Range"),
      };
    }

    let startDate = todayDateKey;
    let label = "Last 30 Days";
    if (regActivityRangePreset === "90d") {
      startDate = addDaysToDateKeyUTC(todayDateKey, -89) || todayDateKey;
      label = "Last 90 Days";
    } else if (regActivityRangePreset === "6m") {
      startDate = addMonthsToDateKeyUTC(todayDateKey, -6) || todayDateKey;
      label = "Last 6 Months";
    } else if (regActivityRangePreset === "1y") {
      startDate = addMonthsToDateKeyUTC(todayDateKey, -12) || todayDateKey;
      label = "Last 1 Year";
    } else {
      startDate = addDaysToDateKeyUTC(todayDateKey, -29) || todayDateKey;
    }

    return {
      preset: regActivityRangePreset,
      allTime: false,
      startDate,
      endDate: todayDateKey,
      label: buildLabel(startDate, todayDateKey, label),
    };
  }, [
    regActivityRangePreset,
    regActivityCustomStartDate,
    regActivityCustomEndDate,
    utcMsToDateKey,
    formatDateKeyRangeShortUTC,
    isDateKeyUTC,
    addDaysToDateKeyUTC,
    addMonthsToDateKeyUTC,
  ]);

  const regActivityMeasureOptions = useMemo(
    () => [
      { value: "daily", label: "Daily" },
      { value: "weekly", label: "Weekly" },
      { value: "monthly", label: "Monthly" },
      { value: "yearly", label: "Yearly" },
    ],
    []
  );

  const registrationActivityChartData = useMemo(() => {
    const dailyRows = (Array.isArray(regActivityRows) ? regActivityRows : [])
      .map((row) => {
        const dateKey = String(row?.dateKey || "").trim();
        const ms = dateKeyToUtcMs(dateKey);
        if (!dateKey || !Number.isFinite(ms)) return null;
        return {
          bucketType: "daily",
          bucketKey: dateKey,
          bucketLabel: dateKey,
          xLabel: formatDateKeyShortUTC(dateKey),
          dateKey,
          sortMs: ms,
          currentRegistrations: toAbsInt(row?.currentRegistrations || 0),
          newUsers: toAbsInt(row?.newUsers || 0),
          falloutBidders: toAbsInt(row?.falloutBidders || 0),
          revivedBidders: toAbsInt(row?.revivedBidders || 0),
          doaBidders: toAbsInt(row?.doaBidders || 0),
        };
      })
      .filter(Boolean);

    if (regActivityMeasure === "daily") return dailyRows;

    const bucketMap = new Map();
    dailyRows.forEach((row) => {
      const dateKey = String(row?.dateKey || "").trim();
      if (!dateKey) return;

      if (regActivityMeasure === "weekly") {
        const weekStartKey = weekStartDateKeyUTC(dateKey);
        const weekEndKey = addDaysToDateKeyUTC(weekStartKey, 6);
        const sortMs = dateKeyToUtcMs(weekStartKey);
        if (!weekStartKey || !Number.isFinite(sortMs)) return;
        const bucketKey = weekStartKey;
        const existing = bucketMap.get(bucketKey) || {
          bucketType: "weekly",
          bucketKey,
          bucketLabel: `${formatDateKeyShortUTC(weekStartKey)} - ${formatDateKeyShortUTC(weekEndKey)}`,
          xLabel: `${formatDateKeyShortUTC(weekStartKey)}`,
          dateKey: "",
          sortMs,
          currentRegistrations: 0,
          newUsers: 0,
          falloutBidders: 0,
          revivedBidders: 0,
          doaBidders: 0,
        };
        existing.currentRegistrations += toAbsInt(row?.currentRegistrations || 0);
        existing.newUsers += toAbsInt(row?.newUsers || 0);
        existing.falloutBidders += toAbsInt(row?.falloutBidders || 0);
        existing.revivedBidders += toAbsInt(row?.revivedBidders || 0);
        existing.doaBidders += toAbsInt(row?.doaBidders || 0);
        bucketMap.set(bucketKey, existing);
        return;
      }

      if (regActivityMeasure === "yearly") {
        const yearKey = String(dateKey).slice(0, 4);
        const yearMs = Date.parse(`${yearKey}-01-01T00:00:00.000Z`);
        if (!/^\d{4}$/.test(yearKey) || !Number.isFinite(yearMs)) return;
        const bucketKey = yearKey;
        const existing = bucketMap.get(bucketKey) || {
          bucketType: "yearly",
          bucketKey,
          bucketLabel: yearKey,
          xLabel: yearKey,
          dateKey: "",
          sortMs: yearMs,
          currentRegistrations: 0,
          newUsers: 0,
          falloutBidders: 0,
          revivedBidders: 0,
          doaBidders: 0,
        };
        existing.currentRegistrations += toAbsInt(row?.currentRegistrations || 0);
        existing.newUsers += toAbsInt(row?.newUsers || 0);
        existing.falloutBidders += toAbsInt(row?.falloutBidders || 0);
        existing.revivedBidders += toAbsInt(row?.revivedBidders || 0);
        existing.doaBidders += toAbsInt(row?.doaBidders || 0);
        bucketMap.set(bucketKey, existing);
        return;
      }

      const monthKey = String(dateKey).slice(0, 7);
      const monthMs = Date.parse(`${monthKey}-01T00:00:00.000Z`);
      if (!monthKey || !Number.isFinite(monthMs)) return;
      const bucketKey = monthKey;
      const existing = bucketMap.get(bucketKey) || {
        bucketType: "monthly",
        bucketKey,
        bucketLabel: monthLabelFromKeyUTC(monthKey),
        xLabel: monthLabelFromKeyUTC(monthKey),
        dateKey: "",
        sortMs: monthMs,
        currentRegistrations: 0,
        newUsers: 0,
        falloutBidders: 0,
        revivedBidders: 0,
        doaBidders: 0,
      };
      existing.currentRegistrations += toAbsInt(row?.currentRegistrations || 0);
      existing.newUsers += toAbsInt(row?.newUsers || 0);
      existing.falloutBidders += toAbsInt(row?.falloutBidders || 0);
      existing.revivedBidders += toAbsInt(row?.revivedBidders || 0);
      existing.doaBidders += toAbsInt(row?.doaBidders || 0);
      bucketMap.set(bucketKey, existing);
    });

    return Array.from(bucketMap.values()).sort((a, b) => safeNum(a?.sortMs) - safeNum(b?.sortMs));
  }, [
    regActivityRows,
    regActivityMeasure,
    dateKeyToUtcMs,
    formatDateKeyShortUTC,
    toAbsInt,
    weekStartDateKeyUTC,
    addDaysToDateKeyUTC,
    monthLabelFromKeyUTC,
    safeNum,
  ]);

  const sortedRegistrationActivityRows = useMemo(() => {
    const base = Array.isArray(registrationActivityChartData) ? registrationActivityChartData : [];
    const dir = regActivitySortDir === "asc" ? 1 : -1;
    const getVal = (row) => {
      switch (regActivitySortKey) {
        case "currentRegistrations":
          return toAbsInt(row?.currentRegistrations || 0);
        case "newUsers":
          return toAbsInt(row?.newUsers || 0);
        case "falloutBidders":
          return toAbsInt(row?.falloutBidders || 0);
        case "revivedBidders":
          return toAbsInt(row?.revivedBidders || 0);
        case "doaBidders":
          return toAbsInt(row?.doaBidders || 0);
        case "dateKey":
        default:
          return toAbsInt(row?.sortMs || 0);
      }
    };
    return [...base].sort((a, b) => compareSortValues(getVal(a), getVal(b)) * dir);
  }, [registrationActivityChartData, regActivitySortKey, regActivitySortDir, toAbsInt, compareSortValues]);

  const registrationActivityHasData = useMemo(
    () =>
      registrationActivityChartData.some(
        (row) =>
          toAbsInt(row?.currentRegistrations || 0) > 0 ||
          toAbsInt(row?.newUsers || 0) > 0 ||
          toAbsInt(row?.falloutBidders || 0) > 0 ||
          toAbsInt(row?.revivedBidders || 0) > 0 ||
          toAbsInt(row?.doaBidders || 0) > 0
      ),
    [registrationActivityChartData, toAbsInt]
  );

  const fetchRegistrationActivityData = useCallback(async ({ month = "", startDate = "", endDate = "", allTime = false } = {}) => {
    if (!orgReady) return;
    const mk = String(month || "").trim();
    const startDateKey = String(startDate || "").trim();
    const endDateKey = String(endDate || "").trim();
    const allTimeFlag = !!allTime;
    if (!mk && !allTimeFlag && !startDateKey && !endDateKey) return;

    setRegActivityLoading(true);
    try {
      const qs = new URLSearchParams();
      if (mk) qs.set("month", mk);
      if (startDateKey) qs.set("startDate", startDateKey);
      if (endDateKey) qs.set("endDate", endDateKey);
      if (allTimeFlag) qs.set("allTime", "true");
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.registrationActivityMonthly}?${qs.toString()}`);
      if (handleNeedsAuthRef.current(res, data)) {
        setRegActivityRows([]);
        setRegActivityTotals(createEmptyTotals());
        return;
      }
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const totals = data?.totals && typeof data.totals === "object" ? data.totals : {};
      setRegActivityRows(rows);
      setRegActivityTotals({
        currentRegistrations: toAbsInt(totals?.currentRegistrations || 0),
        newUsers: toAbsInt(totals?.newUsers || 0),
        falloutBidders: toAbsInt(totals?.falloutBidders || 0),
        revivedBidders: toAbsInt(totals?.revivedBidders || 0),
        doaBidders: toAbsInt(totals?.doaBidders || 0),
      });
    } catch (e) {
      setRegActivityRows([]);
      setRegActivityTotals(createEmptyTotals());
      toastOpenRef.current(e?.message || "Failed to load registration activity", "error");
    } finally {
      setRegActivityLoading(false);
    }
  }, [orgReady, apiBaseUrl, routes.registrationActivityMonthly, toAbsInt]);

  const fetchRegistrationActivityUsers = useCallback(async ({ dateKey, activityType }) => {
    if (!orgReady) return;
    const dk = String(dateKey || "").trim();
    const type = String(activityType || "").trim();
    if (!dk || !type) return;

    setRegActivityUsersOpen(true);
    setRegActivityUsersLoading(true);
    setRegActivityUsersError("");
    setRegActivityUsersDateKey(dk);
    setRegActivityUsersType(type);
    setRegActivityUsersRows([]);
    try {
      const qs = new URLSearchParams();
      qs.set("dateKey", dk);
      qs.set("activityType", type);
      qs.set("limit", "1000");
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.registrationActivityUsers}?${qs.toString()}`);
      if (handleNeedsAuthRef.current(res, data)) {
        setRegActivityUsersRows([]);
        return;
      }
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setRegActivityUsersRows(
        rows.map((r) => ({
          id: String(r?.id || ""),
          bidderName: String(r?.bidderName || "").trim(),
          username: String(r?.username || "").trim(),
          company: String(r?.company || "").trim(),
          email: String(r?.email || "").trim().toLowerCase(),
          detectedAtMs: Number(r?.detectedAtMs || 0),
          detectedAt: String(r?.detectedAt || "").trim(),
          auctionName: String(r?.auctionName || "").trim(),
          auctionId: String(r?.auctionId || "").trim(),
          previousAuctionName: String(r?.previousAuctionName || "").trim(),
          previousAuctionId: String(r?.previousAuctionId || "").trim(),
          previousRegistrationAtMs: Number(r?.previousRegistrationAtMs || 0),
          previousRegistrationAt: String(r?.previousRegistrationAt || "").trim(),
        }))
      );
    } catch (e) {
      setRegActivityUsersRows([]);
      setRegActivityUsersError(e?.message || "Failed to load activity users.");
    } finally {
      setRegActivityUsersLoading(false);
    }
  }, [orgReady, apiBaseUrl, routes.registrationActivityUsers]);

  const closeRegActivityUsersDialog = useCallback(() => {
    setRegActivityUsersOpen(false);
  }, []);

  useEffect(() => {
    if (!orgReady) return;
    if (tab !== 2) return;
    fetchRegistrationActivityData({
      startDate: regActivityResolvedRange.startDate,
      endDate: regActivityResolvedRange.endDate,
      allTime: regActivityResolvedRange.allTime,
    });
  }, [
    orgReady,
    tab,
    regActivityResolvedRange.startDate,
    regActivityResolvedRange.endDate,
    regActivityResolvedRange.allTime,
    fetchRegistrationActivityData,
  ]);

  return {
    regActivityRangePreset,
    setRegActivityRangePreset,
    regActivityCustomStartDate,
    setRegActivityCustomStartDate,
    regActivityCustomEndDate,
    setRegActivityCustomEndDate,
    regActivityMeasure,
    setRegActivityMeasure,
    regActivityTotals,
    regActivityLoading,
    regActivitySortKey,
    setRegActivitySortKey,
    regActivitySortDir,
    setRegActivitySortDir,
    regActivityUsersOpen,
    regActivityUsersLoading,
    regActivityUsersError,
    regActivityUsersDateKey,
    regActivityUsersType,
    regActivityUsersRows,
    regActivityRangeOptions,
    regActivityResolvedRange,
    regActivityMeasureOptions,
    registrationActivityChartData,
    sortedRegistrationActivityRows,
    registrationActivityHasData,
    fetchRegistrationActivityData,
    fetchRegistrationActivityUsers,
    closeRegActivityUsersDialog,
  };
}
